/*
 * The Transactions screen's state.
 *
 * Search is debounced and every filter resets the offset, because this list is paged by the server
 * with no total — changing a filter without resetting lands you on page four of a different query.
 *
 * `reveal` is the audited one. A ticket link is a capability: holding it opens somebody's ticket.
 * The server returns it once, per reveal, and writes an audit row — so the value is held in
 * component state, never merged into the row, and dropped whenever the query changes.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, superadminApi } from '../../../api';
import type { SuperadminOperationDto } from '../../../api';

export interface SuperOperationsState {
  rows: SuperadminOperationDto[];
  loading: boolean;
  loadError: string;
  search: string;
  setSearch: (v: string) => void;
  eventFilter: string;
  setEventFilter: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  /** Null when the server says there is no next page. */
  nextOffset: number | null;
  loadMore: () => void;
  loadingMore: boolean;
  revealedLinks: Record<string, string>;
  revealBusy: string;
  revealError: string;
  reveal: (linkId: string) => Promise<void>;
}

export function useSuperOperations(token: string): SuperOperationsState {
  const [rows, setRows] = useState<SuperadminOperationDto[]>([]);
  const [search, setSearchState] = useState('');
  const [eventFilter, setEventFilterState] = useState('');
  const [statusFilter, setStatusFilterState] = useState('');
  const [offset, setOffset] = useState(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [revealedLinks, setRevealedLinks] = useState<Record<string, string>>({});
  const [revealBusy, setRevealBusy] = useState('');
  const [revealError, setRevealError] = useState('');

  useEffect(() => {
    let active = true;
    if (offset === 0) setLoading(true); else setLoadingMore(true);
    setLoadError('');
    // Revealed capabilities do not survive a query change. Nothing should be able to scroll back
    // to a ticket link that was opened under a different filter.
    if (offset === 0) setRevealedLinks({});
    const timer = window.setTimeout(() => {
      superadminApi.listOperations(token, {
        q: search.trim() || undefined,
        eventId: eventFilter || undefined,
        status: statusFilter || undefined,
        limit: 50,
        offset,
      }).then((page) => {
        if (!active) return;
        setRows((prev) => (offset === 0 ? page.items : [...prev, ...page.items]));
        setNextOffset(page.nextOffset);
      }).catch(() => {
        if (active) setLoadError('โหลดข้อมูลธุรกรรมไม่สำเร็จ');
      }).finally(() => {
        if (!active) return;
        setLoading(false);
        setLoadingMore(false);
      });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [token, search, eventFilter, statusFilter, offset]);

  // Every filter resets the page. A paged list with no total cannot keep its offset across a query.
  const resetting = useCallback(<T,>(set: (v: T) => void) => (v: T) => { set(v); setOffset(0); }, []);

  return {
    rows, loading, loadError,
    search, setSearch: resetting(setSearchState),
    eventFilter, setEventFilter: resetting(setEventFilterState),
    statusFilter, setStatusFilter: resetting(setStatusFilterState),
    nextOffset, loadingMore,
    loadMore: () => { if (nextOffset !== null) setOffset(nextOffset); },
    revealedLinks, revealBusy, revealError,
    reveal: async (linkId) => {
      setRevealBusy(linkId);
      setRevealError('');
      try {
        const { url } = await superadminApi.revealTicketLink(token, linkId);
        setRevealedLinks((prev) => ({ ...prev, [linkId]: url }));
      } catch (e) {
        setRevealError(e instanceof ApiError ? e.message : 'เปิดเผยลิงก์ไม่สำเร็จ');
      } finally {
        setRevealBusy('');
      }
    },
  };
}
